<template>
  <div
    :class="{ isMobile }"
    class="base-select control"
  >
    <label class="label">
      <span class="is-semi-bold font-sm">
        <slot />
      </span>
    </label>

    <component
      :is="variantComponent.default"
      v-bind="variantBindings"
      v-on="$listeners"
    />

    <small
      v-if="errorMessage"
      class="has-text-danger"
    >{{ errorMessage }}</small>

    <small
      v-if="info"
      class="has-text-info"
      v-html="info"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import NativeSelect from './NativeSelect.vue';
import FilterableSelect from './FilterableSelect.vue';
import FeaturedSelect from './FeaturedSelect.vue';

export default {
  name: 'BaseSelect',
  components: {
    NativeSelect,
    FilterableSelect,
    FeaturedSelect,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Array, Boolean],
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Selecione uma opção',
      required: false,
    },
    info: {
      type: String,
      default: '',
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    searching: {
      type: Boolean,
      default: false,
      required: false,
    },
    errorMessage: {
      type: String,
      default: '',
      required: false,
    },
    canFilter: {
      type: Boolean,
      default: false,
      required: false,
    },
    canAdd: {
      type: Boolean,
      default: false,
      required: false,
    },
    canMultiselect: {
      type: Boolean,
      default: false,
      required: false,
    },
    hasApiSearch: {
      type: Boolean,
      default: false,
      required: false,
    },
    hasDefaultBlank: {
      type: Boolean,
      default: true,
      required: false,
    },
    featured: {
      type: Boolean,
      default: false,
      required: false,
    },
    tabindex: {
      type: [Number, String],
      default: null,
      required: false,
    },
    focusOnLoad: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  computed: {
    ...mapGetters({ isMobile: 'app/isMobile' }),
    isFilterable() {
      return this.canFilter || this.canAdd || this.canMultiselect || this.hasApiSearch;
    },
    variantComponent() {
      if (this.featured) {
        return {
          default: FeaturedSelect,
          name: 'featured',
        };
      }
      if (this.isFilterable) {
        return {
          default: FilterableSelect,
          name: 'filterable',
        };
      }
      return {
        default: NativeSelect,
        name: 'native',
      };
    },
    baseBindings() {
      return {
        ...this.$attrs,
        value: this.value,
        options: this.options,
        placeholder: this.placeholder,
        disabled: this.disabled,
        tabindex: this.tabindex,
      };
    },
    variantBindings() {
      if (this.variantComponent.name === 'filterable') {
        return {
          ...this.baseBindings,
          searching: this.searching,
          canAdd: this.canAdd,
          canMultiselect: this.canMultiselect,
          hasApiSearch: this.hasApiSearch,
          focusOnLoad: this.focusOnLoad,
        };
      }
      if (this.variantComponent.name === 'native') {
        return {
          ...this.baseBindings,
          hasDefaultBlank: this.hasDefaultBlank,
        };
      }
      return this.baseBindings;
    },
  },
};
</script>

<style lang="scss" scoped>
.base-select {
  min-height: 62px;

  .label {
    margin-bottom: 0;

    span {
      font: {
        family: Inter;
        size: 14px;
        weight: 400;
      }
      line-height: 20px;
      color: #666;
    }
  }

  &.isMobile {
    ::v-deep .filterable-select__chips.ground {
      display: none;
    }
  }
}
</style>
